import { enumKeyByValue } from '@app/shared/helpers';
import {
  CouponType,
  Dates,
  AdjustmentMode,
  OwnerType,
  CapitalStructureEventSeverity,
} from '@app/shared/models/contracts/enums/shared-enums';
import {
  CapitalStructureDto,
  EquityRefinancingEventDto,
  EquityRefinancingEventMovementDto,
  EquityRefinancingEventOwnerMovementDto,
  EquityRefinancingEventProRataMovementDto,
  InstrumentCoupon,
  ProjectDto,
} from '@app/shared/models/contracts/project-dto';
import {
  CapitalStructureBuildStructureDto,
  CapitalStructureEventDto,
} from '@app/shared/models/contracts/capital-structure-debt-instrument-dto';

export const getUpdatedMovements = (
  newEventDate: string,
  currentMovements: Record<string, EquityRefinancingEventMovementDto>
) => {
  const updatedMovements: Record<string, EquityRefinancingEventMovementDto> = {};
  Object.entries(currentMovements).forEach(([instrumentId, movement]) => {
    updatedMovements[instrumentId] = {
      ...movement,
      coupon: movement.coupon
        ? {
            ...movement.coupon,
            date: newEventDate,
          }
        : undefined,
    };
  });
  return updatedMovements;
};

export const getOpeningCoupon = (
  buildStructureEventId: string,
  capitalStructureKey: string,
  instrumentId: string | undefined,
  openingProjectState: ProjectDto | undefined
): InstrumentCoupon | undefined => {
  if (!instrumentId || !openingProjectState) {
    return undefined;
  }

  const openingCapitalStructure = openingProjectState.capitalStructures[capitalStructureKey];
  const openingEvent = openingCapitalStructure?.events[buildStructureEventId];
  const openingMovements = openingEvent?.movements;

  if (openingMovements && openingMovements[instrumentId]) {
    return openingMovements[instrumentId]?.coupon ?? undefined;
  }

  return undefined;
};

export const getCurrentCoupon = (
  buildStructureEventId: string,
  events: Record<string, EquityRefinancingEventDto>,
  instrumentId: string | undefined
): InstrumentCoupon | undefined => {
  if (!instrumentId) {
    return undefined;
  }

  const currentMovements = events[buildStructureEventId]?.movements;
  if (currentMovements && currentMovements[instrumentId]) {
    return currentMovements[instrumentId]?.coupon ?? undefined;
  }

  return undefined;
};

export const getEquityItem = (
  instrumentId: string | undefined,
  capitalStructure: CapitalStructureDto,
  project: ProjectDto,
  isEvent: boolean,
  selectedEvent: CapitalStructureEventDto | undefined
) => {
  if (!instrumentId) {
    return null;
  }

  const instrumentDefinition = capitalStructure.instrumentDefinitions[instrumentId];
  const initialValues = capitalStructure.initialValues[instrumentId];
  const selectedInstrument = selectedEvent?.ranks
    .flatMap((rank) => Object.entries(rank.instruments))
    .find(([instrument, _]) => {
      return instrument === instrumentId;
    })?.[1];

  if (!selectedInstrument || !instrumentDefinition) {
    return null;
  }

  const getEventValues = () => {
    let couponValue = null;
    let couponStartDate = null;
    let couponType: ReturnType<typeof enumKeyByValue> | undefined = enumKeyByValue(
      CouponType,
      CouponType.NoCoupon
    );
    let cashPayProportion = 0;
    let ownerMovements: Nullable<Record<string, EquityRefinancingEventOwnerMovementDto>> = null;
    let proRataMovement: Nullable<EquityRefinancingEventProRataMovementDto> = null;
    const movements = capitalStructure.events[selectedEvent.id]?.movements;
    if (movements && instrumentId && selectedEvent && capitalStructure.events[selectedEvent.id]) {
      const instrumentMovements = movements[instrumentId];
      if (Object.keys(movements).includes(instrumentId)) {
        if (instrumentMovements?.coupon) {
          couponValue = instrumentMovements.coupon.value;
          couponStartDate = instrumentMovements.coupon.date;
          couponType = instrumentMovements?.coupon?.type ?? couponType;
          cashPayProportion = instrumentMovements?.coupon?.cashPayProportion ?? 0;
        } else {
          couponStartDate =
            (selectedInstrument.coupon?.date === Dates.MinDate
              ? selectedEvent.date
              : selectedInstrument.coupon?.date) ?? project.investmentDate;
          couponType = selectedInstrument.coupon?.type;
          cashPayProportion = selectedInstrument.coupon?.cashPayProportion ?? 0;
        }

        ownerMovements = instrumentMovements?.ownerMovements;
        proRataMovement = instrumentMovements?.proRataMovement ?? null;
      } else {
        couponStartDate = selectedInstrument.coupon?.date ?? project.investmentDate;
        couponType = selectedInstrument.coupon?.type;
        cashPayProportion = selectedInstrument.coupon?.cashPayProportion ?? 0;
      }
    }

    const previousActiveCoupon = selectedInstrument.previousCoupon;

    const isProRataAmountClosing = proRataMovement?.closingAmount != null;
    const isProRataSharesClosing = proRataMovement?.closingNumberOfShares != null;
    const isProRataAmountDelta = proRataMovement?.amount != null;
    const isProRataSharesDelta = proRataMovement?.numberOfShares != null;

    return {
      ...selectedInstrument,
      ...instrumentDefinition,
      // this is required because this can be null in the capitalStructure but needs a defined value in the form
      isSweetEquity: instrumentDefinition.isSweetEquity ?? false,
      dividendEligible: instrumentDefinition.dividendEligible ?? false,
      proRataAmount:
        proRataMovement?.amount ??
        (isProRataAmountClosing ? selectedInstrument?.totalValueDelta : null),
      proRataShares:
        proRataMovement?.numberOfShares ??
        (isProRataSharesClosing ? selectedInstrument?.totalSharesDelta : null),
      proRataClosingAmount:
        proRataMovement?.closingAmount ??
        (isProRataAmountDelta ? selectedInstrument?.totalValueAfterMovements : null),
      proRataClosingShares:
        proRataMovement?.closingNumberOfShares ??
        (isProRataSharesDelta ? selectedInstrument?.totalSharesAfterMovements : null),
      proRataAmountInputMode: isProRataAmountClosing
        ? AdjustmentMode.Absolute
        : isProRataAmountDelta
        ? AdjustmentMode.Relative
        : null,
      proRataSharesInputMode: isProRataSharesClosing
        ? AdjustmentMode.Absolute
        : isProRataSharesDelta
        ? AdjustmentMode.Relative
        : null,
      proRataRealBuybackAmount: proRataMovement?.realBuybackAmount ?? null,
      instrumentId: instrumentId,
      coupon: {
        value: couponValue != null ? couponValue * 100 : null,
        date: couponStartDate ?? project.investmentDate,
        type: couponType,
        cashPayProportion: cashPayProportion * 100,
      },
      previousActiveCoupon: previousActiveCoupon,
      ownership: Object.keys(OwnerType).map((owner) => {
        const isOwnerAmountClosing = ownerMovements?.[owner]?.closingAmount != null;
        const isOwnerSharesClosing = ownerMovements?.[owner]?.closingNumberOfShares != null;
        const isOwnerAmountDelta = ownerMovements?.[owner]?.amount != null;
        const isOwnerSharesDelta = ownerMovements?.[owner]?.numberOfShares != null;

        return {
          owner: owner as keyof typeof OwnerType,
          amount:
            ownerMovements && Object.keys(ownerMovements).includes(owner)
              ? ownerMovements[owner]?.amount ??
                (isOwnerAmountClosing ? selectedInstrument?.valueDeltas[owner] : null)
              : null,
          numberOfShares:
            ownerMovements && Object.keys(ownerMovements).includes(owner)
              ? ownerMovements[owner]?.numberOfShares ??
                (isOwnerSharesClosing ? selectedInstrument?.shareDeltas[owner] : null)
              : null,
          currentAmount: selectedInstrument.ownershipAfterMovements
            ? selectedInstrument.ownershipAfterMovements[owner]?.amount
            : null,
          currentNumberOfShares: selectedInstrument.ownershipAfterMovements
            ? selectedInstrument.ownershipAfterMovements[owner]?.numberOfShares
            : null,
          closingAmount:
            ownerMovements && Object.keys(ownerMovements).includes(owner)
              ? ownerMovements[owner]?.closingAmount ??
                (isOwnerAmountDelta
                  ? selectedInstrument?.ownershipAfterMovements[owner]?.amount
                  : null)
              : null,
          closingNumberOfShares:
            ownerMovements && Object.keys(ownerMovements).includes(owner)
              ? ownerMovements[owner]?.closingNumberOfShares ??
                (isOwnerSharesDelta
                  ? selectedInstrument?.ownershipAfterMovements[owner]?.numberOfShares
                  : null)
              : null,
          realBuybackAmount:
            ownerMovements && Object.keys(ownerMovements).includes(owner)
              ? ownerMovements[owner]?.realBuybackAmount ?? null
              : null,
          amountInputMode:
            ownerMovements && Object.keys(ownerMovements).includes(owner)
              ? isOwnerAmountClosing
                ? AdjustmentMode.Absolute
                : isOwnerAmountDelta
                ? AdjustmentMode.Relative
                : null
              : null,
          numberOfSharesInputMode:
            ownerMovements && Object.keys(ownerMovements).includes(owner)
              ? isOwnerSharesClosing
                ? AdjustmentMode.Absolute
                : isOwnerSharesDelta
                ? AdjustmentMode.Relative
                : null
              : null,
        };
      }),
    };
  };

  const getInitialStructureValues = () => {
    const couponValues = capitalStructure.initialValues[instrumentId].coupon;
    return {
      ...selectedInstrument,
      ...instrumentDefinition,
      // this is required because this can be null in the capitalStructure but needs a defined value in the form
      isSweetEquity: instrumentDefinition.isSweetEquity ?? false,
      dividendEligible: instrumentDefinition.dividendEligible ?? false,
      instrumentId: instrumentId!,
      coupon: {
        value: (couponValues?.value ?? 0) * 100,
        date: couponValues?.date ?? project.investmentDate,
        type: couponValues?.type ?? enumKeyByValue(CouponType, CouponType.NoCoupon),
        cashPayProportion: (couponValues?.cashPayProportion ?? 0) * 100,
      },
      ownership: Object.keys(OwnerType).map((owner) => {
        return {
          owner: owner as keyof typeof OwnerType,
          amount: initialValues?.ownership[owner]?.amount ?? null,
          numberOfShares: initialValues?.ownership[owner]?.numberOfShares ?? null,
          currentAmount: null,
          currentNumberOfShares: null,
        };
      }),
    };
  };

  const getValues = () => {
    const values = isEvent ? getEventValues() : getInitialStructureValues();
    return values;
  };

  return getValues();
};

export const findEarliestNextEventDate = (
  currentEventId: string,
  capitalStructures: CapitalStructureBuildStructureDto
): string | undefined => {
  const { eventSets } = capitalStructures;
  const filteredEventSets = Object.fromEntries(
    Object.entries(eventSets).filter(([, set]) =>
      set.events.some((event) => event.id === currentEventId)
    )
  );
  const nextDates: string[] = [];

  Object.values(filteredEventSets || {}).forEach((set) => {
    const sequenceEntries = Object.entries(set.sequence || {}).filter(([_, seq]) => seq.relevant);
    // Sort by the numeric key
    sequenceEntries.sort((a, b) => parseInt(a[0], 10) - parseInt(b[0], 10));

    // Build an ordered list of event IDs
    const orderedIds = sequenceEntries.map(([_, seq]) => seq.eventId);

    // Find current event index in the orderedIds list
    const thisIndex = orderedIds.indexOf(currentEventId);
    // Check next one by position in sequence
    if (thisIndex !== -1 && thisIndex < orderedIds.length - 1) {
      const nextId = orderedIds[thisIndex + 1];
      const nextEvent = (set.events || []).find((ev) => ev.id === nextId);
      if (nextEvent?.date) {
        nextDates.push(nextEvent.date);
      }
    }
  });

  if (!nextDates.length) {
    return undefined;
  }
  nextDates.sort();
  return nextDates[0];
};

export const findLatestPreviousEventDate = (
  currentEventId: string,
  capitalStructures: CapitalStructureBuildStructureDto
): string | undefined => {
  const { eventSets } = capitalStructures;
  const filteredEventSets = Object.fromEntries(
    Object.entries(eventSets).filter(([, set]) =>
      set.events.some((event) => event.id === currentEventId)
    )
  );
  const previousDates: string[] = [];

  Object.values(filteredEventSets || {}).forEach((set) => {
    const sequenceEntries = Object.entries(set.sequence || {}).filter(([_, seq]) => seq.relevant);
    sequenceEntries.sort((a, b) => parseInt(a[0], 10) - parseInt(b[0], 10));

    const orderedIds = sequenceEntries.map(([_, seq]) => seq.eventId);
    const thisIndex = orderedIds.indexOf(currentEventId);

    if (thisIndex > 0) {
      const prevId = orderedIds[thisIndex - 1];
      const prevEvent = (set.events || []).find((ev) => ev.id === prevId);
      if (prevEvent?.date) {
        previousDates.push(prevEvent.date);
      }
    }
  });

  if (!previousDates.length) {
    return undefined;
  }

  previousDates.sort();
  return previousDates[previousDates.length - 1];
};

export const previousEventHasErrors = (
  selectedEvent: CapitalStructureEventDto | undefined,
  buildStructure: CapitalStructureBuildStructureDto,
  eventSetId: string
): boolean => {
  if (!selectedEvent || selectedEvent.date === undefined) {
    return false;
  }

  const previousEvents = buildStructure.eventSets[eventSetId]?.events
    .filter((event) => event.date < selectedEvent.date)
    .map((event) => event.id);

  const previousEventsFaults = buildStructure.eventSets[eventSetId]?.faults.filter(
    (fault) =>
      previousEvents.includes(fault.event ?? '') &&
      fault.severity === CapitalStructureEventSeverity.Error
  );

  return previousEventsFaults?.length > 0;
};

export const areCouponsEqual = (a?: InstrumentCoupon, b?: InstrumentCoupon): boolean =>
  a?.value === b?.value && a?.type === b?.type && a?.cashPayProportion === b?.cashPayProportion;
